<script lang="ts">
	import type { RouteWithGeometry } from '$lib/components//map/types';
	import Button from '$lib/components/atoms/button/Button.svelte';
	import Link from '$lib/components/atoms/link/Link.svelte';
	import { distanceUnitLabel } from '$lib/components/constants';
	import Section from '$lib/components/molecules/section/Section.svelte';
	import { getLanguageCode } from '$lib/i18n/language';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import { formatPrice } from '$lib/utils/formatPrice';
	import * as m from '$paraglide/messages';
	import { onMount } from 'svelte';
	import { getExpenseNotePrice } from '../getExpenseNotePrice';
	import { getExpenseNoteContext } from './expense-note-context';
	import ExpenseNoteLine from './ExpenseNoteLine.svelte';

	interface Props {
		route: RouteWithGeometry;
		from: string;
		to: string;
		locale: string;
		onEditExpenseNote: () => void;
	}

	const {
		route,
		from,
		to,
		locale,
		onEditExpenseNote,
	}: Props = $props();

	const generatedDate = new Intl.DateTimeFormat(locale).format(new Date());
	const expenseNote = getExpenseNoteContext();
	const {
		mileageAllowance,
		additionalInfo,
		date,
		identity,
		includeTolls,
		includeVignettes,
		computeMethod,
		includeEnergy,
		energyCostPerUnit,
	} = $expenseNote;

	onMount(() => {
		$expenseNote.energyCostPerUnit = route.energyCost?.amount.toString() ?? '0';
	});

	function formatAmount(amount: number) {
		return formatPrice({
			amount,
			// TODO: change this when trip option currency select feature is done
			currency: 'EUR',
			language: getLanguageCode(),
			digits: 2,
		});
	}

	const KILOMETER_TO_METERS = 1000;
	const MILE_TO_FEET = 5280;
	const mileageAllowanceDistance
		= $derived(route.routeDistance.unit === 'METER'
			? route.routeDistance.value / KILOMETER_TO_METERS
			: route.routeDistance.unit === 'FOOT'
			? route.routeDistance.value / MILE_TO_FEET
			: route.routeDistance.value);
	const mileageAllowanceAmount
		= $derived(mileageAllowanceDistance && Number.parseFloat(mileageAllowance) * mileageAllowanceDistance);
	const energyAmount
		= $derived(Number.parseFloat(energyCostPerUnit)
			* ((route.isEv ? route.electricityConsumption : route.fuelConsumption) ?? 0));

	const totalPriceAmount = $derived(getExpenseNotePrice({
		mileageAllowanceAmount,
		energyAmount,
		includeEnergy,
		includeTolls,
		includeVignettes,
		computeMethod,
		routeCosts: route.costs,
	}));

	const mileageAllowancePrice = $derived(formatAmount(mileageAllowanceAmount));
	const energyPrice = $derived(formatAmount(energyAmount));
	const tollsPrice = $derived(formatAmount(route.costs.tolls.amount));
	const vignettesPrice = $derived(formatAmount(route.costs.vignette.amount));
	const totalPrice = $derived(formatAmount(totalPriceAmount));
	const printSearchParams = $derived(new URLSearchParams({
		mileageAllowance: mileageAllowancePrice,
		energy: energyPrice,
		tolls: tollsPrice,
		vignettes: vignettesPrice,
		totalPrice,
		additionalInfo,
		date,
		identity,
		includeTolls: includeTolls.toString(),
		includeVignettes: includeVignettes.toString(),
		includeEnergy: includeEnergy.toString(),
		computeMethod,
		from,
		to,
		distance: `${route.routeDistance.value} ${distanceUnitLabel[route.routeDistance.unit as keyof typeof distanceUnitLabel]}`,
		isEv: (route.isEv ?? false).toString(),
		locale, // Needed on staging preview and local as long as we can't determine the language from the domain name
	}));

	const printUrl = $derived(`/print?${printSearchParams.toString()}`);
</script>

<div class="grid gap-4 rd-lg bg-white shadow-smooth-block p-2 pt-3" data-testid="expense-note">
	<div class="flex flex-row justify-between flex-row-reverse align-center">
		<div class="flex gap-2">
			<Button
				text={m.action__edit()}
				size="md"
				onclick={onEditExpenseNote}
				variant="filled"
				color="light"
			/>
			<Link
				button="filled"
				label={m.action__print()}
				size="md"
				color="light"
				href={printUrl}
				target="_blank"
				onclick={() =>
					track({
						action: 'itinerary_print',
						payload: {
							action: 'print',
						},
					})}
			/>
		</div>
		<div class="text-heading-semibold">
			{m.route_details__expense_note()}
			<div class="text-sm font-normal text-coloredneutral-soft">
				{m.route_details__expense_note_section__generated_on({ date: generatedDate })}
			</div>
		</div>
	</div>

	{#if additionalInfo}
		<Section title={m.route_details__expense_note_section__notes()}>
			<div>{additionalInfo}</div>
		</Section>
	{/if}

	<div class="section text-md">
		{@render NoteLine({ title: m.route_details__expense_note_section__date(), value: date })}
		{@render NoteLine({ title: m.route_details__expense_note_section__identity(), value: identity })}
		{@render NoteLine({ title: m.route_details__expense_note_section__departure(), value: from })}
		{@render NoteLine({ title: m.route_details__expense_note_section__arrival(), value: to })}
		{@render NoteLine({ title: m.route_details__expense_note_section__distance(), value: `${route.routeDistance.value} ${distanceUnitLabel[route.routeDistance.unit as keyof typeof distanceUnitLabel]}` })}
	</div>

	<div class="section text-md">
		{#if computeMethod === 'allowance'}
			{@render NoteLine({ title: m.route_details__expense_note_section__allowance(), value: mileageAllowancePrice })}
		{:else if includeEnergy}
			{@render NoteLine({ title: route.isEv
				? m.route_details__expense_note_section__electricity()
				: m.route_details__expense_note_section__fuel(), value: energyPrice })}
		{/if}
		{#if includeTolls}
			{@render NoteLine({ title: m.route_details__expense_note_section__tolls(), value: tollsPrice })}
		{/if}
		{#if includeVignettes}
			{@render NoteLine({ title: m.route_details__expense_note_section__vignette(), value: vignettesPrice })}
		{/if}
		{@render NoteLine({ title: m.route_details__expense_note_section__total(), value: totalPrice })}
	</div>
</div>

{#snippet NoteLine({ title, value }: { title: string; value: string })}
	<ExpenseNoteLine
		{title}
		{value}
		truncateValue
	/>
{/snippet}

<style>
	.section {
		display: grid;
		grid-template-columns: minmax(0, 2fr) minmax(0, 2fr);
		gap: 8px;
		row-gap: 8px;
		column-gap: 16px;
	}
</style>
